/**
 * Product Stamp Label System v3.0
 * Redesigned & Optimized - All contexts supported
 * 완전히 재설계된 라벨 시스템 - 모든 문제 해결
 * 
 * Features:
 * - 상품 카드 및 상품 페이지 모두 지원
 * - 텍스트, 이미지, SVG 라벨 완전 작동
 * - 오프셋/마진 시스템 정확하게 작동
 * - 포지션 정렬 올바르게 수정
 * - 애니메이션 효과 완전 작동 (transform 충돌 해결)
 */

class ProductStampLabelManager {
  constructor() {
    this.labels = [];
    this.defaultConfig = {
      enabled: true,
      position: 'top-left',
      labelType: 'text',
      labelText: 'US Warehouse',
      labelColor: '#ff6b6b',
      labelTextColor: '#ffffff',
      labelOpacity: 100,
      labelSize: 'medium',
      labelRotation: 0,
      labelPreset: 'default',
      labelAnimation: 'none',
      labelSvgStyle: 'circle',
      labelSvgText: 'US Warehouse',
      labelSvgColor: '#ff6b6b',
      labelSvgTextColor: '#ffffff',
      labelImage: null,
      labelSVG: null,
      offsetX: 0,
      offsetY: 0,
      margin: 12,
      labelScale: 100,
      labelShadow: 8,
      labelFontSize: 13,
      showForTags: [],
      priority: 1,
      hideMobile: false,
      mobileScale: 80,
      customCSS: ''
    };
    
    this.init();
  }

  init() {
    this.loadConfiguration();
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeLabels();
        this.observeProductCards();
      });
    } else {
      this.initializeLabels();
      this.observeProductCards();
    }
    
    // Shopify 테마 에디터 지원
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', () => this.reinitialize());
      document.addEventListener('shopify:section:reorder', () => this.reinitialize());
      document.addEventListener('shopify:section:select', () => this.reinitialize());
    }
  }

  loadConfiguration() {
    // window.stampLabelConfig 우선 사용
    if (window.stampLabelConfig) {
      this.config = { ...this.defaultConfig, ...window.stampLabelConfig };
      return;
    }

    // Meta 태그에서 설정 로드
    const configMeta = document.querySelector('meta[name="stamp-label-config"]');
    if (configMeta) {
      try {
        const configData = JSON.parse(configMeta.content);
        this.config = { ...this.defaultConfig, ...configData };
        return;
      } catch (e) {
        console.warn('[Stamp Label] Failed to parse config from meta tag:', e);
      }
    }

    this.config = { ...this.defaultConfig };
  }

  initializeLabels() {
    if (!this.config.enabled) {
      return;
    }

    // 제품 카드에만 라벨 추가 (상품 페이지에서는 Liquid 템플릿이 처리)
    const productCards = document.querySelectorAll('.product-card__image__container');
    productCards.forEach(container => {
      this.addLabelToProduct(container);
    });
  }

  addLabelToProduct(container) {
    // 이미 라벨이 있으면 스킵
    if (container.querySelector('.product-stamp-label')) {
      return;
    }

    const productCard = container.closest('.product-card');
    if (!productCard) return;

    const productData = this.getProductData(productCard);

    if (!this.shouldShowLabel(productData)) {
      return;
    }

    const label = this.createLabel(productData, 'card');
    if (label) {
      container.appendChild(label);
      this.labels.push({ 
        element: label, 
        productId: productData.id, 
        data: productData,
        context: 'card'
      });
    }
  }

  getProductData(productCard) {
    const tags = productCard.dataset.tags || '';
    const handle = productCard.dataset.handle || '';
    const id = productCard.dataset.productId || productCard.id;
    const tagArray = tags ? tags.split(',').map(tag => tag.trim().toLowerCase()) : [];

    return {
      id: id,
      handle: handle,
      tags: tagArray,
      element: productCard
    };
  }

  shouldShowLabel(productData) {
    // showForTags가 비어있으면 모든 제품에 표시
    if (!this.config.showForTags || this.config.showForTags.length === 0) {
      return true;
    }

    const requiredTags = this.config.showForTags.map(tag => 
      typeof tag === 'string' ? tag.toLowerCase().trim() : tag
    );

    return productData.tags.some(tag => 
      requiredTags.includes(tag.toLowerCase())
    );
  }

  createLabel(productData, context = 'card') {
    const label = document.createElement('div');
    label.className = this.getLabelClasses(context);
    label.id = `stamp-label-${productData.id}-${Date.now()}`;
    label.dataset.priority = this.config.priority;
    label.dataset.labelType = this.config.labelType;
    label.dataset.context = context;
    label.setAttribute('role', 'img');
    label.setAttribute('aria-label', this.config.labelText || 'Product Label');
    
    // CSS 변수 적용
    this.setLabelStyles(label);

    // 라벨 타입에 따라 콘텐츠 생성
    const content = this.createLabelContent();
    if (content) {
      label.appendChild(content);
      return label;
    }

    return null;
  }

  getLabelClasses(context = 'card') {
    const classes = [
      'product-stamp-label',
      `stamp-label--${this.config.position}`,
      `stamp-label-context--${context}`,
      `stamp-label--${this.config.labelType}`,
      `stamp-label--${this.config.labelSize}`
    ];

    // 프리셋 스타일
    if (this.config.labelPreset && this.config.labelPreset !== 'default') {
      classes.push(`stamp-label-preset--${this.config.labelPreset}`);
    }

    // 애니메이션
    if (this.config.labelAnimation && this.config.labelAnimation !== 'none') {
      classes.push(`stamp-label-animation--${this.config.labelAnimation}`);
    }

    // 모바일 숨김
    if (this.config.hideMobile) {
      classes.push('stamp-label--hide-mobile');
    }

    return classes.join(' ');
  }

  setLabelStyles(label) {
    const opacity = this.config.labelOpacity / 100;
    const rotation = this.config.labelRotation || 0;
    const scale = this.config.labelScale / 100 || 1;
    const offsetX = this.config.offsetX || 0;
    const offsetY = this.config.offsetY || 0;
    const margin = this.config.margin || 12;
    const shadow = this.config.labelShadow || 8;
    const fontSize = this.config.labelFontSize || 13;
    const mobileScale = this.config.mobileScale / 100 || 0.8;
    
    // CSS 변수 설정 - 모든 스타일은 CSS로 처리
    label.style.setProperty('--stamp-opacity', opacity);
    label.style.setProperty('--stamp-rotation', `${rotation}deg`);
    label.style.setProperty('--stamp-scale', scale);
    label.style.setProperty('--stamp-offset-x', `${offsetX}px`);
    label.style.setProperty('--stamp-offset-y', `${offsetY}px`);
    label.style.setProperty('--stamp-margin', `${margin}px`);
    label.style.setProperty('--stamp-shadow', `${shadow}px`);
    label.style.setProperty('--stamp-mobile-scale', mobileScale);
    label.style.setProperty('--stamp-bg-color', this.config.labelColor);
    label.style.setProperty('--stamp-text-color', this.config.labelTextColor);
    label.style.setProperty('--stamp-font-size', `${fontSize}px`);

    // 커스텀 CSS 추가
    if (this.config.customCSS) {
      label.style.cssText += this.config.customCSS;
    }
  }

  createLabelContent() {
    const contentWrapper = document.createElement('div');
    contentWrapper.className = 'stamp-label__content';

    switch (this.config.labelType) {
      case 'text':
        return this.createTextLabel();
      case 'image':
        return this.createImageLabel();
      case 'svg':
        return this.createSVGLabel();
      default:
        return this.createTextLabel();
    }
  }

  createTextLabel() {
    const textDiv = document.createElement('div');
    textDiv.className = 'stamp-label__content stamp-label__text';
    textDiv.textContent = this.config.labelText || 'Label';
    textDiv.style.backgroundColor = `var(--stamp-bg-color)`;
    textDiv.style.color = `var(--stamp-text-color)`;
    return textDiv;
  }

  createImageLabel() {
    if (!this.config.labelImage) {
      console.warn('[Stamp Label] Image label requested but no image URL provided, falling back to text');
      return this.createTextLabel();
    }

    const imgContainer = document.createElement('div');
    imgContainer.className = 'stamp-label__content stamp-label__image';
    
    const img = document.createElement('img');
    img.src = this.config.labelImage;
    img.alt = this.config.labelText || 'Product Label';
    img.loading = 'lazy';
    img.className = 'stamp-label__image-element';
    
    imgContainer.appendChild(img);
    return imgContainer;
  }

  createSVGLabel() {
    const svgContainer = document.createElement('div');
    svgContainer.className = 'stamp-label__content stamp-label__svg';
    
    // 커스텀 SVG가 제공된 경우
    if (this.config.labelSVG && this.config.labelSvgStyle === 'custom') {
      svgContainer.innerHTML = this.config.labelSVG;
    } else {
      // 프리셋 SVG 스타일 사용
      svgContainer.innerHTML = this.getSVGForStyle(this.config.labelSvgStyle || 'circle');
    }
    
    return svgContainer;
  }

  getSVGForStyle(style) {
    const text = this.config.labelSvgText || this.config.labelText || 'LABEL';
    const words = text.split(' ');
    const color = this.config.labelSvgColor || this.config.labelColor || '#ff6b6b';
    const textColor = this.config.labelSvgTextColor || this.config.labelTextColor || '#ffffff';
    
    switch (style) {
      case 'hexagon':
        return this.getHexagonSVG(words, color, textColor);
      case 'star':
        return this.getStarSVG(words, color, textColor);
      case 'shield':
        return this.getShieldSVG(words, color, textColor);
      case 'ribbon':
        return this.getRibbonSVG(text, color, textColor);
      case 'triangle-corner':
        return this.getTriangleCornerSVG(text, color, textColor);
      case 'circular-text':
        return this.getCircularTextSVG(text, color, textColor);
      case 'top-bottom-bar':
        return this.getTopBottomBarSVG(text, color, textColor);
      case 'corner-flag':
        return this.getCornerFlagSVG(text, color, textColor);
      case 'circle':
      default:
        return this.getCircleSVG(words, color, textColor);
    }
  }

  getCircleSVG(words, color, textColor) {
    const id = `shadow-${Date.now()}`;
    let textElements = '';
    
    if (words.length === 1) {
      textElements = `<text x="60" y="65" text-anchor="middle" fill="${textColor}" font-size="16" font-weight="bold" font-family="Arial, sans-serif">${words[0]}</text>`;
    } else if (words.length === 2) {
      textElements = `
        <text x="60" y="55" text-anchor="middle" fill="${textColor}" font-size="14" font-weight="bold" font-family="Arial, sans-serif">${words[0]}</text>
        <text x="60" y="72" text-anchor="middle" fill="${textColor}" font-size="14" font-weight="bold" font-family="Arial, sans-serif">${words[1]}</text>
      `;
    } else {
      textElements = `
        <text x="60" y="48" text-anchor="middle" fill="${textColor}" font-size="12" font-weight="bold" font-family="Arial, sans-serif">${words[0]}</text>
        <text x="60" y="63" text-anchor="middle" fill="${textColor}" font-size="12" font-weight="bold" font-family="Arial, sans-serif">${words[1] || ''}</text>
        <text x="60" y="78" text-anchor="middle" fill="${textColor}" font-size="12" font-weight="bold" font-family="Arial, sans-serif">${words[2] || ''}</text>
      `;
    }

    return `
      <svg width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" class="stamp-svg stamp-svg--circle">
        <defs>
          <filter id="${id}" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
            <feOffset dx="0" dy="2" result="offsetblur"/>
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.3"/>
            </feComponentTransfer>
            <feMerge>
              <feMergeNode/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <circle cx="60" cy="60" r="54" fill="${color}" stroke="${textColor}" stroke-width="3" stroke-dasharray="5,3" filter="url(#${id})"/>
        ${textElements}
        <circle cx="60" cy="60" r="50" fill="none" stroke="${textColor}" stroke-width="2"/>
      </svg>
    `;
  }

  getHexagonSVG(words, color, textColor) {
    const id = `shadow-${Date.now()}`;
    const text = words.length === 1 ? words[0] : `${words[0]}${words[1] ? '\n' + words[1] : ''}`;
    
    return `
      <svg width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" class="stamp-svg stamp-svg--hexagon">
        <defs>
          <filter id="${id}" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
            <feOffset dx="0" dy="2"/>
            <feComponentTransfer><feFuncA type="linear" slope="0.3"/></feComponentTransfer>
            <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <polygon points="60,15 90,35 90,75 60,95 30,75 30,35" fill="${color}" stroke="${textColor}" stroke-width="3" filter="url(#${id})"/>
        ${words.length === 1 
          ? `<text x="60" y="65" text-anchor="middle" fill="${textColor}" font-size="15" font-weight="bold" font-family="Arial, sans-serif">${words[0]}</text>`
          : `<text x="60" y="55" text-anchor="middle" fill="${textColor}" font-size="13" font-weight="bold" font-family="Arial, sans-serif">${words[0]}</text>
             <text x="60" y="72" text-anchor="middle" fill="${textColor}" font-size="13" font-weight="bold" font-family="Arial, sans-serif">${words[1] || ''}</text>`
        }
      </svg>
    `;
  }

  getStarSVG(words, color, textColor) {
    const id = `shadow-${Date.now()}`;
    
    return `
      <svg width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" class="stamp-svg stamp-svg--star">
        <defs>
          <filter id="${id}" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
            <feOffset dx="0" dy="2"/>
            <feComponentTransfer><feFuncA type="linear" slope="0.3"/></feComponentTransfer>
            <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <polygon points="60,15 70,45 100,45 76,63 85,93 60,75 35,93 44,63 20,45 50,45" fill="${color}" stroke="${textColor}" stroke-width="2" filter="url(#${id})"/>
        <text x="60" y="58" text-anchor="middle" fill="${textColor}" font-size="12" font-weight="bold" font-family="Arial, sans-serif">${words[0]}</text>
        ${words.length > 1 ? `<text x="60" y="72" text-anchor="middle" fill="${textColor}" font-size="11" font-weight="bold" font-family="Arial, sans-serif">${words[1]}</text>` : ''}
      </svg>
    `;
  }

  getShieldSVG(words, color, textColor) {
    const id = `shadow-${Date.now()}`;
    
    return `
      <svg width="120" height="130" viewBox="0 0 120 130" xmlns="http://www.w3.org/2000/svg" class="stamp-svg stamp-svg--shield">
        <defs>
          <filter id="${id}" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
            <feOffset dx="0" dy="2"/>
            <feComponentTransfer><feFuncA type="linear" slope="0.3"/></feComponentTransfer>
            <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <path d="M60,10 L95,25 L95,65 Q95,90 60,115 Q25,90 25,65 L25,25 Z" fill="${color}" stroke="${textColor}" stroke-width="3" filter="url(#${id})"/>
        ${words.length === 1 
          ? `<text x="60" y="68" text-anchor="middle" fill="${textColor}" font-size="14" font-weight="bold" font-family="Arial, sans-serif">${words[0]}</text>`
          : `<text x="60" y="58" text-anchor="middle" fill="${textColor}" font-size="13" font-weight="bold" font-family="Arial, sans-serif">${words[0]}</text>
             <text x="60" y="75" text-anchor="middle" fill="${textColor}" font-size="12" font-weight="bold" font-family="Arial, sans-serif">${words[1] || ''}</text>`
        }
      </svg>
    `;
  }

  getRibbonSVG(text, color, textColor) {
    const id = `shadow-${Date.now()}`;
    
    return `
      <svg width="140" height="80" viewBox="0 0 140 80" xmlns="http://www.w3.org/2000/svg" class="stamp-svg stamp-svg--ribbon">
        <defs>
          <filter id="${id}" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
            <feOffset dx="0" dy="2"/>
            <feComponentTransfer><feFuncA type="linear" slope="0.3"/></feComponentTransfer>
            <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <path d="M10,25 L120,25 L135,40 L120,55 L10,55 L10,25 Z" fill="${color}" stroke="${textColor}" stroke-width="2" filter="url(#${id})"/>
        <path d="M10,25 L0,15 L0,30 Z" fill="${color}" stroke="${textColor}" stroke-width="1"/>
        <path d="M10,55 L0,65 L0,50 Z" fill="${color}" stroke="${textColor}" stroke-width="1"/>
        <text x="65" y="45" text-anchor="middle" fill="${textColor}" font-size="14" font-weight="bold" font-family="Arial, sans-serif">${text}</text>
      </svg>
    `;
  }

  getTriangleCornerSVG(text, color, textColor) {
    const id = `shadow-${Date.now()}`;
    
    return `
      <svg width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" class="stamp-svg stamp-svg--triangle-corner">
        <defs>
          <filter id="${id}" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
            <feOffset dx="0" dy="2"/>
            <feComponentTransfer><feFuncA type="linear" slope="0.3"/></feComponentTransfer>
            <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <polygon points="0,0 100,0 100,100" fill="${color}" stroke="${textColor}" stroke-width="2" filter="url(#${id})"/>
        <text x="35" y="35" text-anchor="middle" fill="${textColor}" font-size="12" font-weight="bold" font-family="Arial, sans-serif" transform="rotate(-45 35 35)">${text}</text>
      </svg>
    `;
  }

  getCircularTextSVG(text, color, textColor) {
    const id = `shadow-${Date.now()}`;
    const radius = 45;
    const centerX = 60;
    const centerY = 60;
    
    // 텍스트를 원형으로 배치하기 위한 계산
    const characters = text.split('');
    let textElements = '';
    
    characters.forEach((char, index) => {
      const angle = (index / characters.length) * 2 * Math.PI - Math.PI / 2; // 12시 방향부터 시작
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      const rotate = (angle * 180) / Math.PI + 90; // 텍스트를 원에 tangential하게 회전
      
      textElements += `<text x="${x}" y="${y}" text-anchor="middle" fill="${textColor}" font-size="14" font-weight="bold" font-family="Arial, sans-serif" transform="rotate(${rotate} ${x} ${y})">${char}</text>`;
    });
    
    return `
      <svg width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" class="stamp-svg stamp-svg--circular-text">
        <defs>
          <filter id="${id}" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
            <feOffset dx="0" dy="2"/>
            <feComponentTransfer><feFuncA type="linear" slope="0.3"/></feComponentTransfer>
            <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <circle cx="${centerX}" cy="${centerY}" r="${radius + 5}" fill="${color}" stroke="${textColor}" stroke-width="2" filter="url(#${id})"/>
        <circle cx="${centerX}" cy="${centerY}" r="${radius}" fill="none" stroke="${textColor}" stroke-width="1"/>
        ${textElements}
      </svg>
    `;
  }

  getTopBottomBarSVG(text, color, textColor) {
    const id = `shadow-${Date.now()}`;
    
    return `
      <svg width="140" height="40" viewBox="0 0 140 40" xmlns="http://www.w3.org/2000/svg" class="stamp-svg stamp-svg--top-bottom-bar">
        <defs>
          <filter id="${id}" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
            <feOffset dx="0" dy="2"/>
            <feComponentTransfer><feFuncA type="linear" slope="0.3"/></feComponentTransfer>
            <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <rect x="0" y="0" width="140" height="40" rx="20" ry="20" fill="${color}" stroke="${textColor}" stroke-width="2" filter="url(#${id})"/>
        <text x="70" y="25" text-anchor="middle" fill="${textColor}" font-size="14" font-weight="bold" font-family="Arial, sans-serif">${text}</text>
      </svg>
    `;
  }

  getCornerFlagSVG(text, color, textColor) {
    const id = `shadow-${Date.now()}`;
    
    return `
      <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" class="stamp-svg stamp-svg--corner-flag">
        <defs>
          <filter id="${id}" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
            <feOffset dx="0" dy="2"/>
            <feComponentTransfer><feFuncA type="linear" slope="0.3"/></feComponentTransfer>
            <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <polygon points="0,0 80,0 80,80" fill="${color}" stroke="${textColor}" stroke-width="2" filter="url(#${id})"/>
        <polygon points="80,0 100,0 80,20" fill="${color}" stroke="${textColor}" stroke-width="2"/>
        <text x="25" y="25" text-anchor="middle" fill="${textColor}" font-size="10" font-weight="bold" font-family="Arial, sans-serif" transform="rotate(-45 25 25)">${text}</text>
      </svg>
    `;
  }

  observeProductCards() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // 직접 product-card__image__container인 경우
            if (node.classList && node.classList.contains('product-card__image__container')) {
              this.addLabelToProduct(node);
            }
            
            // 하위에 product-card__image__container가 있는 경우
            const containers = node.querySelectorAll ? node.querySelectorAll('.product-card__image__container') : [];
            containers.forEach(container => {
              this.addLabelToProduct(container);
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    this.observer = observer;
  }

  reinitialize() {
    // 기존 라벨 제거
    this.labels.forEach(label => {
      if (label.element && label.element.parentNode) {
        label.element.parentNode.removeChild(label.element);
      }
    });
    
    this.labels = [];
    
    // 설정 다시 로드
    this.loadConfiguration();
    
    // 라벨 다시 초기화
    this.initializeLabels();
  }

  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    this.reinitialize();
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    this.labels.forEach(label => {
      if (label.element && label.element.parentNode) {
        label.element.parentNode.removeChild(label.element);
      }
    });
    
    this.labels = [];
  }

  // 디버그용 메서드
  enableDebugMode() {
    document.body.classList.add('stamp-label-debug');
    console.log('[Stamp Label] Debug mode enabled', {
      config: this.config,
      labels: this.labels
    });
  }

  disableDebugMode() {
    document.body.classList.remove('stamp-label-debug');
  }
}

// Helper function
function createStampLabel(config) {
  const manager = new ProductStampLabelManager();
  manager.updateConfig(config);
  return manager;
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.stampLabelManager = new ProductStampLabelManager();
    console.log('[Stamp Label] System initialized v3.0');
  });
} else {
  window.stampLabelManager = new ProductStampLabelManager();
  console.log('[Stamp Label] System initialized v3.0');
}

// Export to global scope
window.ProductStampLabelManager = ProductStampLabelManager;
window.createStampLabel = createStampLabel;
